import React, { useState, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMapEvents,
  useMap,
  LayersControl,
  LayerGroup,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from '../../assets/icons/RedMap.svg';
import markerIconRetina from '../../assets/icons/RedMap.svg';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css';
import { LocateControl } from 'leaflet.locatecontrol';
import { useLocation } from 'react-router-dom';
import { useMapSettings } from '../Context/MapSettingsContext';
import { useLocalization } from '../../components/Context/MultiLanguageContext';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface MapAddAddressProps {
  onLocationChange: (
    lat: string,
    lon: string,
    pinCode: string,
    street: string,
    city: string
  ) => void;
  searchedLocation?: { lat: number; lng: number } | null;
}

const DEFAULT_CENTER: [number, number] = [9.997147, 76.300737];

const LocateControlComponent = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const locateOptions = {
      position: 'topright' as L.ControlPosition,
      drawCircle: false,
      setView: (m: L.Map, latlng: L.LatLng, options: any) => {
        m.setView(latlng, options.maxZoom);
      },
      locateOptions: {
        enableHighAccuracy: true,
        maxZoom: 16,
      },
    } as any;

    const locateControl = new LocateControl(locateOptions).addTo(map);
    locateControl.start();

    return () => {
      locateControl.stop();
      map.removeControl(locateControl);
    };
  }, [map]);

  return null;
};

const MapAddAddress: React.FC<MapAddAddressProps> = ({
  onLocationChange,
  searchedLocation = null,
}) => {
  const location = useLocation();
  const { t } = useLocalization();

  const queryParams = new URLSearchParams(location.search);
  const latQuery = queryParams.get('latitude');
  const lonQuery = queryParams.get('longitude');
  const { zoom, layer } = useMapSettings();

  const [markerPosition, setMarkerPosition] = useState<[number, number]>(DEFAULT_CENTER);
  const [pincode, setPincode] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');

  const updateLocationDetails = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);

    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then((response) => response.json())
      .then((data) => {
        const pinCodeValue = data.address?.postcode || 'N/A';
        const streetValue = data.address?.road || 'N/A';
        const cityValue =
          data.address?.city || data.address?.town || data.address?.village || 'N/A';

        setPincode(pinCodeValue);
        setStreet(streetValue);
        setCity(cityValue);

        onLocationChange(
          lat.toString(),
          lng.toString(),
          pinCodeValue,
          streetValue,
          cityValue
        );
      })
      .catch((error) => console.error('Error fetching location details:', error));
  };

  useEffect(() => {
    if (searchedLocation) {
      updateLocationDetails(searchedLocation.lat, searchedLocation.lng);
      return;
    }

    if (latQuery && lonQuery) {
      updateLocationDetails(parseFloat(latQuery), parseFloat(lonQuery));
    } else {
      const saved = localStorage.getItem('selectedHomeMapLocation');

      if (saved) {
        const { lat, lng } = JSON.parse(saved);
        updateLocationDetails(lat, lng);
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            updateLocationDetails(latitude, longitude);
          },
          () => {
            updateLocationDetails(DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
          }
        );
      }
    }
  }, [latQuery, lonQuery, searchedLocation]);

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        updateLocationDetails(lat, lng);
      },
    });
    return null;
  };

  const MapMover: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();

    useEffect(() => {
      map.setView(center, map.getZoom());
    }, [center, map]);

    return null;
  };

  return (
    <div className="relative">
      <MapContainer center={markerPosition} zoom={zoom} className="h-[400px] w-full">
        <LayersControl position="topright">
          <LayersControl.BaseLayer
            name={t('defaultView')}
            checked={layer !== 'Satellite View'}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxNativeZoom={19}
              maxZoom={22}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer
            name={t('satelliteView')}
            checked={layer === 'Satellite View'}
          >
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={19}
                maxZoom={22}
                attribution="&copy; Esri"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
                opacity={0.8}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        <Marker position={markerPosition} icon={defaultIcon}>
          {(street || city || pincode) && (
            <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
              <span>
                {street}, {city}, {pincode}
              </span>
            </Tooltip>
          )}
        </Marker>

        <MapClickHandler />
        <MapMover center={markerPosition} />
        <LocateControlComponent />
      </MapContainer>
    </div>
  );
};

export default MapAddAddress;